import { useEffect } from 'react';

/**
 * Garde l'écran allumé tant que la page est au premier plan (Screen Wake Lock API). Le navigateur
 * libère le verrou dès que la page passe en arrière-plan : on le redemande à chaque retour. Sans
 * support ou en cas de refus, rien ne change : la consigne à l'écran fait le reste.
 */
export function useScreenWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const request = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        // Démontage pendant la requête : le nettoyage est déjà passé, ce verrou-là n'a personne
        // pour le relâcher, l'écran resterait allumé après la sortie.
        if (cancelled) {
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel = lock;
      } catch {
        sentinel = null;
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void request();
    };

    void request();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      void sentinel?.release().catch(() => undefined);
    };
  }, [enabled]);
}
